import React, { useEffect, useMemo, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  Typography,
  Avatar,
  TextField,
  Box,
  Tab,
  Tabs,
  Card,
  CardContent,
  IconButton,
  Divider,
  Alert,
  FormHelperText,
  CircularProgress
} from '@mui/material';
import { Formik, Form, Field } from 'formik';
import * as Yup from 'yup';
import { isValidPhoneNumber } from 'libphonenumber-js';
import AppDialogTitle from '../ui/appDialogTitle';
import PhoneNumberField from '../common/phoneNumberField';
import { useBusinessPreferences } from 'hooks/usePreferences';
import { Colors } from '../../styles/theme';
import PhotoCamera from '@mui/icons-material/PhotoCamera';
import PersonIcon from '@mui/icons-material/Person';
import ContactPhoneIcon from '@mui/icons-material/ContactPhone';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import SecurityIcon from '@mui/icons-material/Security';
import { passwordStrength } from 'utils';
import { PasswordStrengthLinearProgress } from '../../styles/passwordStrengthProgress';
import { PasswordField } from '../common/passwordField';
import { AuthService } from '../../service/authService';
import { EmployeeService } from '../../service/employeeService';
import useAbortController from 'hooks/useAbortController';
import { PASSWORD_POLICY_REGEX, PASSWORD_POLICY_HINT, PASSWORD_POLICY_MESSAGE } from '../../utils/passwordPolicy';

/**
 * User Profile Dialog Component
 *
 * Allows users to view and edit their profile information.
 * Organized into tabs for better user experience and data management.
 *
 * Features:
 * - Personal information editing
 * - Contact details management
 * - Address information
 * - Emergency contacts
 * - Password change functionality
 * - Profile photo upload
 * - Form validation with Yup
 * - Responsive design
 */
const UserProfileDialog = ({ open, onClose, user }) => {
  const [activeTab, setActiveTab] = useState(0);
  const [profilePhoto, setProfilePhoto] = useState(null);
  const [saveStatus, setSaveStatus] = useState(null); // 'success', 'error', or null
  const [saveMessage, setSaveMessage] = useState('');
  const [passwordStrengthLevel, setPasswordStrengthLevel] = useState(null);
  const [isLoadingProfile, setIsLoadingProfile] = useState(false);
  const [profileError, setProfileError] = useState(null);
  const [profileValues, setProfileValues] = useState(null);
  const [signal] = useAbortController();

  // Get business preferences for phone formatting
  const { preferences: businessPreferences } = useBusinessPreferences();
  const phoneFormat = businessPreferences?.phoneNumberFormat || '(###) ###-####';
  const isProfileReadOnly = true;

  const emptyProfileValues = useMemo(() => ({
    id: user?.id || '',
    title: '',
    firstName: '',
    middleName: '',
    lastName: '',
    phone: '',
    email: '',
    addrNo: '',
    addrUnit: '',
    addrStreet: '',
    addrCity: '',
    addrProvince: '',
    addrPostal: '',
    emergencyContactName1: '',
    emergencyContactPhone1: '',
    emergencyContactName2: '',
    emergencyContactPhone2: '',
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  }), [user?.id]);

  useEffect(() => {
    if (open) {
      setSaveStatus(null);
      setSaveMessage('');
    }
  }, [open]);

  useEffect(() => {
    if (!open || !user?.id || !signal) {
      return;
    }

    let mounted = true;
    setIsLoadingProfile(true);
    setProfileError(null);
    setProfilePhoto(null);

    (async () => {
      try {
        const response = await EmployeeService.getById(user.id, signal);
        if (!mounted) {
          return;
        }

        if (response?.code === 0) {
          const data = response.data || {};
          setProfileValues({
            id: data.id,
            title: data.title || '',
            firstName: data.firstName || '',
            middleName: data.middleName || '',
            lastName: data.lastName || '',
            phone: data.phone || '',
            email: data.email || '',
            addrNo: data.addrNo || '',
            addrUnit: data.addrUnit || '',
            addrStreet: data.addrStreet || '',
            addrCity: data.addrCity || '',
            addrProvince: data.addrProvince || '',
            addrPostal: data.addrPostal || '',
            emergencyContactName1: data.emergencyContactName1 || '',
            emergencyContactPhone1: data.emergencyContactPhone1 || '',
            emergencyContactName2: data.emergencyContactName2 || '',
            emergencyContactPhone2: data.emergencyContactPhone2 || '',
            currentPassword: '',
            newPassword: '',
            confirmPassword: ''
          });

          setProfilePhoto(data.avatarUrl || null);
        } else {
          setProfileValues(emptyProfileValues);
          setProfileError(response?.message || 'Unable to load profile.');
        }
      } catch (error) {
        if (!mounted || error?.name === 'CanceledError' || error?.name === 'AbortError') {
          return;
        }
        setProfileError('Unable to load profile.');
        setProfileValues(emptyProfileValues);
      } finally {
        if (mounted) {
          setIsLoadingProfile(false);
        }
      }
    })();

    return () => {
      mounted = false;
    };
  }, [open, user?.id, signal, emptyProfileValues]);

  /**
   * Validation schema for personal information
   */
  const personalInfoSchema = Yup.object({
    title: Yup.string().max(60, 'Title must be 60 characters or less'),
    firstName: Yup.string().required('First name is required').max(60, 'First name must be 60 characters or less'),
    middleName: Yup.string().max(60, 'Middle name must be 60 characters or less'),
    lastName: Yup.string().required('Last name is required').max(60, 'Last name must be 60 characters or less')
  });

  /**
   * Validation schema for contact information
   */
  const contactInfoSchema = Yup.object({
    // phone: Yup.string()
    //   .test('valid-phone', 'Please enter a valid phone number', (value) => {
    //     if (!value) return true; // Allow empty values
    //     return isValidPhoneNumber(value, 'US');
    //   }),
    // email: Yup.string().email('Please enter a valid email address').required('Email is required')
  });

  /**
   * Validation schema for address information
   */
  const addressInfoSchema = Yup.object({
    // addrNo: Yup.string().max(20, 'Address number must be 20 characters or less'),
    // addrUnit: Yup.string().max(20, 'Unit must be 20 characters or less'),
    // addrStreet: Yup.string().max(50, 'Street must be 50 characters or less'),
    // addrCity: Yup.string().max(60, 'City must be 60 characters or less'),
    // addrProvince: Yup.string().max(128, 'Province must be 128 characters or less'),
    // addrPostal: Yup.string().max(12, 'Postal code must be 12 characters or less')
  });

  /**
   * Validation schema for emergency contacts
   */
  const emergencyContactSchema = Yup.object({
    // emergencyContactName1: Yup.string().max(60, 'Emergency contact name must be 60 characters or less'),
    // emergencyContactPhone1: Yup.string()
    //   .test('valid-emergency-phone-1', 'Please enter a valid phone number', (value) => {
    //     if (!value) return true; // Allow empty values
    //     return isValidPhoneNumber(value, 'US');
    //   }),
    // emergencyContactName2: Yup.string().max(60, 'Emergency contact name must be 60 characters or less'),
    // emergencyContactPhone2: Yup.string()
    //   .test('valid-emergency-phone-2', 'Please enter a valid phone number', (value) => {
    //     if (!value) return true; // Allow empty values
    //     return isValidPhoneNumber(value, 'US');
    //   })
  });

  /**
   * Validation schema for password change
   */
  const passwordSchema = Yup.object({
    currentPassword: Yup.string().required('Current password is required'),
    newPassword: Yup.string()
      .matches(PASSWORD_POLICY_REGEX, PASSWORD_POLICY_MESSAGE),
    confirmPassword: Yup.string()
      .oneOf([Yup.ref('newPassword'), null], 'Passwords must match')
  });

  /**
   * Combined validation schema for all tabs
   */
  const validationSchema = Yup.object({
    ...personalInfoSchema.fields,
    ...contactInfoSchema.fields,
    ...addressInfoSchema.fields,
    ...emergencyContactSchema.fields,
    ...passwordSchema.fields
  });

  /**
   * Handle profile photo upload
   */
  const handlePhotoUpload = (event) => {
    if (isProfileReadOnly) {
      return;
    }
    const file = event.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        setProfilePhoto(e.target.result);
      };
      reader.readAsDataURL(file);
    }
  };

  /**
   * Handle form submission
   * In a real application, this would call an API
   */
  const handleSubmit = async (values, { setSubmitting, resetForm, setFieldError }) => {
    try {
      console.log('Submitting form:', values);
      setSaveStatus(null);
      setSaveMessage('');

      const response = await AuthService.changePassword(values.currentPassword, values.newPassword);
      const { code, message } = response || {};

      if (code === 0) {
        const clearedValues = {
          ...values,
          currentPassword: '',
          newPassword: '',
          confirmPassword: ''
        };

        setSaveStatus('success');
        setSaveMessage(message || 'Password updated successfully.');
        setPasswordStrengthLevel(null);
        resetForm({ values: clearedValues });
      } else {
        setSaveStatus('error');
        setSaveMessage(message || 'Failed to update password.');
      }

    } catch (error) {
      const errorMessage = error?.response?.data?.message || 'Failed to update password. Please try again.';
      setSaveStatus('error');
      setSaveMessage(errorMessage);

      if (error?.response?.data?.errors?.length) {
        error.response.data.errors.forEach(({ field, message: fieldMessage }) => {
          if (field) {
            setFieldError(field, fieldMessage || errorMessage);
          }
        });
      }
    }
    setSubmitting(false);
  };

  /**
   * Handle tab change
   */
  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const formInitialValues = profileValues || emptyProfileValues;

  return (
    <Dialog open={open} maxWidth="lg" fullWidth>
      <AppDialogTitle title="User Profile" onClose={onClose} />
      <Formik
        initialValues={formInitialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
        enableReinitialize
      >
        {({ isSubmitting, errors, touched, values, setFieldValue }) => (
          <Form>
            <DialogContent sx={{ p: 0 }}>
              {/* Status Alert */}
              {saveStatus === 'success' && (
                <Alert severity="success" sx={{ m: 2 }}>
                  {saveMessage || 'Password updated successfully!'}
                </Alert>
              )}
              {saveStatus === 'error' && (
                <Alert severity="error" sx={{ m: 2 }}>
                  {saveMessage || 'Failed to update password. Please try again.'}
                </Alert>
              )}
              {profileError && (
                <Alert severity="error" sx={{ m: 2 }}>
                  {profileError}
                </Alert>
              )}

              {isLoadingProfile && (
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 120, my: 2 }}>
                  <CircularProgress size={32} />
                </Box>
              )}

              {/* Profile Photo Section */}
              <Box sx={{ p: 3, bgcolor: Colors.muted, display: 'flex', alignItems: 'center', gap: 3 }}>
                <Box sx={{ position: 'relative' }}>
                  <Avatar
                    src={profilePhoto || user?.avatarUrl}
                    alt={user?.name || 'User'}
                    sx={{ width: 80, height: 80 }}
                  >
                    {!profilePhoto && !user?.avatarUrl && <PersonIcon sx={{ fontSize: 40 }} />}
                  </Avatar>
                  <IconButton
                    component="label"
                    disabled={isProfileReadOnly}
                    sx={{
                      position: 'absolute',
                      bottom: -8,
                      right: -8,
                      bgcolor: Colors.primary,
                      color: 'white',
                      '&:hover': { bgcolor: Colors.primary, opacity: 0.8 }
                    }}
                  >
                    <PhotoCamera sx={{ fontSize: 16 }} />
                    <input
                      hidden
                      accept="image/*"
                      type="file"
                      disabled={isProfileReadOnly}
                      onChange={handlePhotoUpload}
                    />
                  </IconButton>
                </Box>
                <Box>
                  <Typography variant="h6">
                    {values.firstName} {values.middleName} {values.lastName}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {values.email}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {user?.role || 'Practitioner'}
                  </Typography>
                </Box>
              </Box>

              {/* Tabs Navigation */}
              <Tabs
                value={activeTab}
                onChange={handleTabChange}
                variant="scrollable"
                scrollButtons="auto"
                sx={{ borderBottom: 1, borderColor: 'divider', px: 3 }}
              >
                <Tab icon={<PersonIcon />} label="Personal" />
                <Tab icon={<ContactPhoneIcon />} label="Contact" />
                <Tab icon={<LocationOnIcon />} label="Address" />
                <Tab icon={<PersonIcon />} label="Emergency" />
                <Tab icon={<SecurityIcon />} label="Security" />
              </Tabs>

              {/* Tab Content */}
              <Box sx={{ p: 3 }}>
                {/* Personal Information Tab */}
                {activeTab === 0 && (
                  <Card>
                    <CardContent>
                      <Typography variant="h6" gutterBottom>
                        Personal Information
                      </Typography>
                      <Divider sx={{ mb: 3 }} />

                      <Grid container spacing={2}>
                        <Grid item xs={12} sm={3}>
                          <Field name="title">
                            {({ field }) => (
                              <TextField
                                {...field}
                                label="Title"
                                fullWidth
                                size="small"
                                    disabled={isProfileReadOnly}
                                error={touched.title && Boolean(errors.title)}
                                helperText={touched.title && errors.title}
                              />
                            )}
                          </Field>
                        </Grid>
                        <Grid item xs={12} sm={3}>
                          <Field name="firstName">
                            {({ field }) => (
                              <TextField
                                {...field}
                                label="First Name"
                                fullWidth
                                size="small"
                                required
                                disabled={isProfileReadOnly}
                                error={touched.firstName && Boolean(errors.firstName)}
                                helperText={touched.firstName && errors.firstName}
                              />
                            )}
                          </Field>
                        </Grid>
                        <Grid item xs={12} sm={3}>
                          <Field name="middleName">
                            {({ field }) => (
                              <TextField
                                {...field}
                                label="Middle Name"
                                fullWidth
                                size="small"
                                disabled={isProfileReadOnly}
                                error={touched.middleName && Boolean(errors.middleName)}
                                helperText={touched.middleName && errors.middleName}
                              />
                            )}
                          </Field>
                        </Grid>
                        <Grid item xs={12} sm={3}>
                          <Field name="lastName">
                            {({ field }) => (
                              <TextField
                                {...field}
                                label="Last Name"
                                fullWidth
                                size="small"
                                required
                                disabled={isProfileReadOnly}
                                error={touched.lastName && Boolean(errors.lastName)}
                                helperText={touched.lastName && errors.lastName}
                              />
                            )}
                          </Field>
                        </Grid>
                      </Grid>
                    </CardContent>
                  </Card>
                )}

                {/* Contact Information Tab */}
                {activeTab === 1 && (
                  <Card>
                    <CardContent>
                      <Typography variant="h6" gutterBottom>
                        Contact Information
                      </Typography>
                      <Divider sx={{ mb: 3 }} />

                      <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                          <Field name="phone">
                            {({ field, form }) => (
                              <PhoneNumberField
                                field={field}
                                form={form}
                                mask={phoneFormat}
                                countryCode="+1"
                                label="Phone Number"
                                fullWidth
                                size="small"
                                disabled={isProfileReadOnly}
                                error={touched.phone && Boolean(errors.phone)}
                                helperText={touched.phone && errors.phone}
                              />
                            )}
                          </Field>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <Field name="email">
                            {({ field }) => (
                              <TextField
                                {...field}
                                label="Email Address"
                                type="email"
                                fullWidth
                                size="small"
                                required
                                disabled={isProfileReadOnly}
                                error={touched.email && Boolean(errors.email)}
                                helperText={touched.email && errors.email}
                              />
                            )}
                          </Field>
                        </Grid>
                      </Grid>
                    </CardContent>
                  </Card>
                )}

                {/* Address Information Tab */}
                {activeTab === 2 && (
                  <Card>
                    <CardContent>
                      <Typography variant="h6" gutterBottom>
                        Address Information
                      </Typography>
                      <Divider sx={{ mb: 3 }} />

                      <Grid container spacing={2}>
                        <Grid item xs={12} sm={4}>
                          <Field name="addrNo">
                            {({ field }) => (
                              <TextField
                                {...field}
                                label="Street Number"
                                fullWidth
                                size="small"
                                disabled={isProfileReadOnly}
                                error={touched.addrNo && Boolean(errors.addrNo)}
                                helperText={touched.addrNo && errors.addrNo}
                              />
                            )}
                          </Field>
                        </Grid>
                        <Grid item xs={12} sm={4}>
                          <Field name="addrUnit">
                            {({ field }) => (
                              <TextField
                                {...field}
                                label="Unit/Apt"
                                fullWidth
                                size="small"
                                disabled={isProfileReadOnly}
                                error={touched.addrUnit && Boolean(errors.addrUnit)}
                                helperText={touched.addrUnit && errors.addrUnit}
                              />
                            )}
                          </Field>
                        </Grid>
                        <Grid item xs={12} sm={4}>
                          <Field name="addrStreet">
                            {({ field }) => (
                              <TextField
                                {...field}
                                label="Street"
                                fullWidth
                                size="small"
                                disabled={isProfileReadOnly}
                                error={touched.addrStreet && Boolean(errors.addrStreet)}
                                helperText={touched.addrStreet && errors.addrStreet}
                              />
                            )}
                          </Field>
                        </Grid>
                        <Grid item xs={12} sm={4}>
                          <Field name="addrCity">
                            {({ field }) => (
                              <TextField
                                {...field}
                                label="City"
                                fullWidth
                                size="small"
                                disabled={isProfileReadOnly}
                                error={touched.addrCity && Boolean(errors.addrCity)}
                                helperText={touched.addrCity && errors.addrCity}
                              />
                            )}
                          </Field>
                        </Grid>
                        <Grid item xs={12} sm={4}>
                          <Field name="addrProvince">
                            {({ field }) => (
                              <TextField
                                {...field}
                                label="Province/State"
                                fullWidth
                                size="small"
                                disabled={isProfileReadOnly}
                                error={touched.addrProvince && Boolean(errors.addrProvince)}
                                helperText={touched.addrProvince && errors.addrProvince}
                              />
                            )}
                          </Field>
                        </Grid>
                        <Grid item xs={12} sm={4}>
                          <Field name="addrPostal">
                            {({ field }) => (
                              <TextField
                                {...field}
                                label="Postal Code"
                                fullWidth
                                size="small"
                                disabled={isProfileReadOnly}
                                error={touched.addrPostal && Boolean(errors.addrPostal)}
                                helperText={touched.addrPostal && errors.addrPostal}
                              />
                            )}
                          </Field>
                        </Grid>
                      </Grid>
                    </CardContent>
                  </Card>
                )}

                {/* Emergency Contacts Tab */}
                {activeTab === 3 && (
                  <Card>
                    <CardContent>
                      <Typography variant="h6" gutterBottom>
                        Emergency Contacts
                      </Typography>
                      <Divider sx={{ mb: 3 }} />

                      <Typography variant="subtitle2" gutterBottom sx={{ mt: 2 }}>
                        Emergency Contact 1
                      </Typography>
                      <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                          <Field name="emergencyContactName1">
                            {({ field }) => (
                              <TextField
                                {...field}
                                label="Contact Name"
                                fullWidth
                                size="small"
                                disabled={isProfileReadOnly}
                                error={touched.emergencyContactName1 && Boolean(errors.emergencyContactName1)}
                                helperText={touched.emergencyContactName1 && errors.emergencyContactName1}
                              />
                            )}
                          </Field>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <Field name="emergencyContactPhone1">
                            {({ field, form }) => (
                              <PhoneNumberField
                                field={field}
                                form={form}
                                mask={phoneFormat}
                                countryCode="+1"
                                label="Contact Phone"
                                fullWidth
                                size="small"
                                disabled={isProfileReadOnly}
                                error={touched.emergencyContactPhone1 && Boolean(errors.emergencyContactPhone1)}
                                helperText={touched.emergencyContactPhone1 && errors.emergencyContactPhone1}
                              />
                            )}
                          </Field>
                        </Grid>
                      </Grid>

                      <Typography variant="subtitle2" gutterBottom sx={{ mt: 3 }}>
                        Emergency Contact 2 (Optional)
                      </Typography>
                      <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                          <Field name="emergencyContactName2">
                            {({ field }) => (
                              <TextField
                                {...field}
                                label="Contact Name"
                                fullWidth
                                size="small"
                                disabled={isProfileReadOnly}
                                error={touched.emergencyContactName2 && Boolean(errors.emergencyContactName2)}
                                helperText={touched.emergencyContactName2 && errors.emergencyContactName2}
                              />
                            )}
                          </Field>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <Field name="emergencyContactPhone2">
                            {({ field, form }) => (
                              <PhoneNumberField
                                field={field}
                                form={form}
                                mask={phoneFormat}
                                countryCode="+1"
                                label="Contact Phone"
                                fullWidth
                                size="small"
                                disabled={isProfileReadOnly}
                                error={touched.emergencyContactPhone2 && Boolean(errors.emergencyContactPhone2)}
                                helperText={touched.emergencyContactPhone2 && errors.emergencyContactPhone2}
                              />
                            )}
                          </Field>
                        </Grid>
                      </Grid>
                    </CardContent>
                  </Card>
                )}

                {/* Security Tab */}
                {activeTab === 4 && (
                  <Card>
                    <CardContent>
                      <Typography variant="h6" gutterBottom>
                        Change Password
                      </Typography>
                      <Divider sx={{ mb: 3 }} />

                      <Alert severity="info" sx={{ mb: 3 }}>
                        {PASSWORD_POLICY_MESSAGE}
                      </Alert>

                      <Grid container spacing={2}>
                        <Grid item xs={12}>
                          <Field name="currentPassword">
                            {({ field }) => (
                              <TextField
                                {...field}
                                label="Current Password"
                                type="password"
                                fullWidth
                                size="small"
                                required
                                error={touched.currentPassword && Boolean(errors.currentPassword)}
                                helperText={touched.currentPassword && errors.currentPassword}
                              />
                            )}
                          </Field>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <Field name="newPassword">
                            {({ field, form }) => (
                              <PasswordField
                                {...field}
                                label="New Password"
                                fieldName="newPassword"
                                autoComplete="new-password"
                                form={form}
                                required
                                error={Boolean(errors.newPassword)}
                                helperText={errors.newPassword}
                                onChange={(e) => {
                                  field.onChange(e);
                                  setPasswordStrengthLevel(passwordStrength(e.target.value));
                                }}
                              />
                            )}
                          </Field>
                          <FormHelperText>{PASSWORD_POLICY_HINT}</FormHelperText>
                          <PasswordStrengthLinearProgress variant="determinate" value={passwordStrengthLevel} strength={passwordStrengthLevel} />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <Field name="confirmPassword">
                            {({ field, form }) => (
                              <PasswordField
                                {...field}
                                label="Confirm New Password"
                                fieldName="confirmPassword"
                                autoComplete="new-password"
                                form={form}
                                required
                                error={Boolean(errors.confirmPassword)}
                                helperText={errors.confirmPassword}
                              />
                            )}
                          </Field>
                        </Grid>
                      </Grid>
                    </CardContent>
                  </Card>
                )}
              </Box>
            </DialogContent>

            <DialogActions sx={{ p: 3, pt: 0 }}>
              <Button onClick={onClose} color="inherit">
                Cancel
              </Button>
              <Button
                type="submit"
                variant="contained"
                  disabled={
                    isSubmitting ||
                    isLoadingProfile ||
                    !values.currentPassword ||
                    !values.newPassword ||
                    !values.confirmPassword
                  }
                sx={{ minWidth: 100 }}
              >
                  {isSubmitting ? 'Saving...' : 'Update Password'}
              </Button>
            </DialogActions>
          </Form>
        )}
      </Formik>
    </Dialog>
  );
};

export default UserProfileDialog;